import * as React from 'react'

/**
 * InlineHelpProvider — context that fetches + caches inline-help topics
 * for the `<HelpBubblePopover>`, `<InlineHelpSheet>`, and
 * `<EmptyState helpTopicId>` consumers in @intelliauth/ui.
 *
 * Three responsibilities:
 *
 *   1. Topic fetch + cache (per (topicId, audience, locale) tuple,
 *      memoised for the provider's lifetime).
 *   2. Seen-state — tracks per-user "what has the user already opened?"
 *      so HelpBubbles pulse-glow when a topic is unseen / updated.
 *      Server-truth via GET /api/v1/inline-docs/seen; updates via a
 *      debounced batched POST.
 *   3. Sheet state — owns the open/close + currentTopic for the
 *      single app-level InlineHelpSheet. Any consumer can call
 *      useInlineHelpSheet().openSheet(topicId) to surface the rich
 *      reading mode.
 *
 * Wire shape mirrors the DP API:
 *
 *   GET  /api/v1/inline-docs/{topicId}?audience=..&locale=..
 *   GET  /api/v1/inline-docs/seen?topic_ids=t1,t2,..
 *   POST /api/v1/inline-docs/seen  body { entries: [{topic_id, version}] }
 *
 * Apps wire `fetcher`, `seenFetcher`, and `markSeenFetcher` so the DS
 * stays HTTP-client-agnostic. seenFetcher/markSeenFetcher are optional
 * — when omitted, seen-state behaves as "everything is unseen" (every
 * bubble pulses) and the mark-on-open call no-ops. v1 apps pass all
 * three; future SDK consumers can stub seen-state.
 *
 * # Knob-off graceful degradation
 *
 * A 204 from the topic endpoint means the tenant has flipped
 * docs.inline_help.enabled off. The provider caches `null` for that
 * key; consumers detect the null result and render `null` themselves
 * (HelpBubble disappears).
 */

/** Closed-set audience per `inline_doc_versions.audience` CHECK constraint. */
export type InlineHelpAudience = 'developer' | 'tenant_admin' | 'cp_admin' | 'end_user'

/** One `see_also` entry as the FE renders it (composed URL, not raw docs_path). */
export interface InlineHelpSeeAlso {
  title: string
  url: string
}

/** The topic shape consumers receive. Mirrors the DP wire envelope's `data`. */
export interface InlineHelpTopic {
  topic_id: string
  audience: InlineHelpAudience
  locale: string
  version: number
  title: string
  body_html: string
  see_also: InlineHelpSeeAlso[]
  content_hash: string
  updated_at: string
}

/** What the provider's fetcher must return for one topic lookup. */
export type InlineHelpFetchResult =
  | { kind: 'topic'; topic: InlineHelpTopic }
  /** 204 — tenant Knob is off. Consumer hides the bubble. */
  | { kind: 'disabled' }
  /** 404 / 410 / network error — consumer can choose to hide or show a fallback. */
  | { kind: 'error'; reason: string }

export type InlineHelpFetcher = (args: {
  topicId: string
  audience: InlineHelpAudience
  locale: string
  signal?: AbortSignal
}) => Promise<InlineHelpFetchResult>

/** Server-truth for "which topics has this user seen and at what version?". */
export interface InlineHelpSeenEntry {
  topic_id: string
  seen_version: number
}

export type InlineHelpSeenFetcher = (topicIds: string[]) => Promise<InlineHelpSeenEntry[]>

export type InlineHelpMarkSeenFetcher = (
  entries: { topic_id: string; version: number }[],
) => Promise<void>

interface InlineHelpContextValue {
  audience: InlineHelpAudience
  locale: string
  docsOrigin: string
  loadTopic: (topicId: string) => Promise<InlineHelpFetchResult>

  /** True when (a) we have the topic loaded AND (b) seen-state says
   * the user hasn't yet seen this version. Used by the bubble's
   * pulsing-dot indicator. Default-true (pulse) until seen-state
   * resolves, so first-time users still get the visual prompt. */
  isUnseen: (topicId: string) => boolean

  /** Mark a topic as seen at its current version. Debounced + batched
   * server-side; safe to call on every popover open. No-op when the
   * markSeenFetcher is not configured. */
  markSeen: (topicId: string) => void

  /** Sheet state: which topic the slide-in panel is showing right now,
   * and whether it's open. */
  sheetTopicId: string | null
  sheetOpen: boolean
  openSheet: (topicId: string) => void
  closeSheet: () => void
}

const InlineHelpContext = React.createContext<InlineHelpContextValue | null>(null)

export interface InlineHelpProviderProps {
  audience: InlineHelpAudience
  fetcher: InlineHelpFetcher
  /** Optional — when present, the provider hydrates seen-state on
   * topic resolution + every consumer of useInlineHelp receives a
   * correct `isUnseen` value. When omitted, every topic is treated
   * as unseen (pulsing always on). */
  seenFetcher?: InlineHelpSeenFetcher
  /** Optional — when present, opening a popover/sheet marks the
   * topic seen via a debounced batched POST. When omitted, mark-seen
   * is a no-op (visual pulse never goes away). */
  markSeenFetcher?: InlineHelpMarkSeenFetcher
  locale?: string
  docsOrigin?: string
  /** How long to batch markSeen calls before flushing the POST. */
  markSeenDebounceMs?: number
  children: React.ReactNode
}

function deriveDocsOrigin(): string {
  if (typeof window === 'undefined') return 'https://docs.intelliauth.local'
  const host = window.location.host
  const firstDot = host.indexOf('.')
  const tail = firstDot >= 0 ? host.slice(firstDot + 1) : host
  return `${window.location.protocol}//docs.${tail}`
}

export const InlineHelpProvider: React.FC<InlineHelpProviderProps> = ({
  audience,
  fetcher,
  seenFetcher,
  markSeenFetcher,
  locale = 'en',
  docsOrigin,
  markSeenDebounceMs = 1500,
  children,
}) => {
  // Topic cache — per (topicId, audience, locale) tuple.
  const cacheRef = React.useRef<Map<string, InlineHelpFetchResult>>(new Map())
  const inflightRef = React.useRef<Map<string, Promise<InlineHelpFetchResult>>>(new Map())
  // Seen-state, by topic_id. Server is the source of truth.
  const [seenVersions, setSeenVersions] = React.useState<Map<string, number>>(new Map())
  // Mark-seen batch buffer. Flushed via a setTimeout(markSeenDebounceMs).
  const pendingMarkRef = React.useRef<Map<string, number>>(new Map())
  const flushTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  // Sheet state — single instance per provider scope.
  const [sheetState, setSheetState] = React.useState<{ open: boolean; topicId: string | null }>(
    { open: false, topicId: null },
  )
  const resolvedDocsOrigin = React.useMemo(() => docsOrigin ?? deriveDocsOrigin(), [docsOrigin])

  const fetchSeenForTopic = React.useCallback(
    async (topicId: string) => {
      if (!seenFetcher) return
      try {
        const rows = await seenFetcher([topicId])
        if (rows.length > 0) {
          setSeenVersions((prev) => {
            const next = new Map(prev)
            for (const row of rows) {
              next.set(row.topic_id, row.seen_version)
            }
            return next
          })
        }
      } catch {
        // Seen-state failure is silent — UI degrades to "always pulsing"
        // which is a worse experience but never breaks the page.
      }
    },
    [seenFetcher],
  )

  const loadTopic = React.useCallback(
    async (topicId: string): Promise<InlineHelpFetchResult> => {
      const key = `${topicId}|${audience}|${locale}`
      const cached = cacheRef.current.get(key)
      if (cached) return cached

      const inflight = inflightRef.current.get(key)
      if (inflight) return inflight

      const promise = (async () => {
        try {
          const result = await fetcher({ topicId, audience, locale })
          if (result.kind === 'topic') {
            // Compose absolute docs URLs from the raw paths.
            const composed: InlineHelpTopic = {
              ...result.topic,
              see_also: result.topic.see_also.map((sa) => ({
                title: sa.title,
                url: sa.url.startsWith('http')
                  ? sa.url
                  : `${resolvedDocsOrigin}${sa.url.startsWith('/') ? sa.url : `/${sa.url}`}`,
              })),
            }
            const finalResult: InlineHelpFetchResult = { kind: 'topic', topic: composed }
            cacheRef.current.set(key, finalResult)
            // Hydrate seen-state for this topic the first time it's
            // loaded. One small GET per first-touch is acceptable;
            // batching N requests into one GET is a future optimisation.
            void fetchSeenForTopic(topicId)
            return finalResult
          }
          cacheRef.current.set(key, result)
          return result
        } finally {
          inflightRef.current.delete(key)
        }
      })()

      inflightRef.current.set(key, promise)
      return promise
    },
    [audience, locale, fetcher, resolvedDocsOrigin, fetchSeenForTopic],
  )

  const flushMarkSeen = React.useCallback(async () => {
    if (!markSeenFetcher) {
      pendingMarkRef.current.clear()
      return
    }
    const batch = Array.from(pendingMarkRef.current.entries()).map(
      ([topicId, version]) => ({ topic_id: topicId, version }),
    )
    pendingMarkRef.current.clear()
    if (batch.length === 0) return
    try {
      await markSeenFetcher(batch)
      // Optimistically update local seen-state so the bubble stops
      // pulsing immediately, not at the next provider mount.
      setSeenVersions((prev) => {
        const next = new Map(prev)
        for (const entry of batch) {
          const prior = next.get(entry.topic_id) ?? 0
          if (entry.version > prior) {
            next.set(entry.topic_id, entry.version)
          }
        }
        return next
      })
    } catch {
      // Same posture as fetchSeenForTopic — silent on failure.
    }
  }, [markSeenFetcher])

  const scheduleFlush = React.useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current)
    }
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null
      void flushMarkSeen()
    }, markSeenDebounceMs)
  }, [flushMarkSeen, markSeenDebounceMs])

  // Flush any in-flight batch on unmount so a quick "open popover →
  // close tab" doesn't lose the mark-seen.
  React.useEffect(() => {
    return () => {
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current)
        flushTimerRef.current = null
      }
      void flushMarkSeen()
    }
  }, [flushMarkSeen])

  const markSeen = React.useCallback(
    (topicId: string) => {
      const key = `${topicId}|${audience}|${locale}`
      const cached = cacheRef.current.get(key)
      if (!cached || cached.kind !== 'topic') {
        // No topic data yet — can't determine the version. The next
        // open after the topic loads will mark correctly.
        return
      }
      pendingMarkRef.current.set(topicId, cached.topic.version)
      scheduleFlush()
    },
    [audience, locale, scheduleFlush],
  )

  const isUnseen = React.useCallback(
    (topicId: string) => {
      const key = `${topicId}|${audience}|${locale}`
      const cached = cacheRef.current.get(key)
      if (!cached || cached.kind !== 'topic') {
        // Topic not loaded yet — default to "unseen" (pulse) so first-
        // time visitors get the visual prompt as soon as the data arrives.
        return true
      }
      const seen = seenVersions.get(topicId) ?? 0
      return cached.topic.version > seen
    },
    [audience, locale, seenVersions],
  )

  const openSheet = React.useCallback(
    (topicId: string) => {
      setSheetState({ open: true, topicId })
      // Opening the rich-reading view counts as "seen" — same posture
      // as the popover open.
      markSeen(topicId)
    },
    [markSeen],
  )

  const closeSheet = React.useCallback(() => {
    setSheetState((prev) => ({ ...prev, open: false }))
  }, [])

  const value = React.useMemo<InlineHelpContextValue>(
    () => ({
      audience,
      locale,
      docsOrigin: resolvedDocsOrigin,
      loadTopic,
      isUnseen,
      markSeen,
      sheetTopicId: sheetState.topicId,
      sheetOpen: sheetState.open,
      openSheet,
      closeSheet,
    }),
    [audience, locale, resolvedDocsOrigin, loadTopic, isUnseen, markSeen, sheetState, openSheet, closeSheet],
  )

  return <InlineHelpContext.Provider value={value}>{children}</InlineHelpContext.Provider>
}
InlineHelpProvider.displayName = 'InlineHelpProvider'

export interface UseInlineHelpResult {
  topic: InlineHelpTopic | null
  loading: boolean
  disabled: boolean
  error: string | null
  /** True when the topic has unseen content (newer than the user's
   * recorded seen_version). Drives the HelpBubble's pulsing-dot. */
  unseen: boolean
  /** Imperative "mark this topic as seen" — call from the popover
   * onOpen callback. The bubble's pulsing-dot disappears after the
   * debounced POST completes. */
  markSeen: () => void
}

export function useInlineHelp(topicId: string): UseInlineHelpResult {
  const ctx = React.useContext(InlineHelpContext)
  if (!ctx) {
    throw new Error('useInlineHelp must be called inside <InlineHelpProvider>')
  }
  const [state, setState] = React.useState<{
    topic: InlineHelpTopic | null
    loading: boolean
    disabled: boolean
    error: string | null
  }>({ topic: null, loading: true, disabled: false, error: null })

  React.useEffect(() => {
    let cancelled = false
    setState({ topic: null, loading: true, disabled: false, error: null })
    ctx
      .loadTopic(topicId)
      .then((res) => {
        if (cancelled) return
        switch (res.kind) {
          case 'topic':
            setState({ topic: res.topic, loading: false, disabled: false, error: null })
            return
          case 'disabled':
            setState({ topic: null, loading: false, disabled: true, error: null })
            return
          case 'error':
            setState({ topic: null, loading: false, disabled: false, error: res.reason })
            return
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const reason = err instanceof Error ? err.message : 'unknown error'
        setState({ topic: null, loading: false, disabled: false, error: reason })
      })
    return () => {
      cancelled = true
    }
  }, [ctx, topicId])

  const unseen = ctx.isUnseen(topicId)
  const markSeen = React.useCallback(() => ctx.markSeen(topicId), [ctx, topicId])

  return { ...state, unseen, markSeen }
}

/**
 * useInlineHelpSheet — gives consumers (EmptyState's "Learn how X
 * works" button, HelpBubblePopover's "Open in sidebar" action) a
 * handle on the single app-level sheet. The InlineHelpSheet host
 * (typically mounted at AdminLayout root) consumes this same value
 * to render the panel.
 */
export interface UseInlineHelpSheetResult {
  open: boolean
  topicId: string | null
  topic: InlineHelpTopic | null
  loading: boolean
  openSheet: (topicId: string) => void
  closeSheet: () => void
}

export function useInlineHelpSheet(): UseInlineHelpSheetResult {
  const ctx = React.useContext(InlineHelpContext)
  if (!ctx) {
    throw new Error('useInlineHelpSheet must be called inside <InlineHelpProvider>')
  }
  // Pull the current topic data when sheet has a topicId selected.
  // Reuses the same loader/cache so opening a sheet for a topic the
  // user has already viewed via a popover is instant.
  const [state, setState] = React.useState<{ topic: InlineHelpTopic | null; loading: boolean }>(
    { topic: null, loading: false },
  )

  React.useEffect(() => {
    if (!ctx.sheetTopicId) {
      setState({ topic: null, loading: false })
      return
    }
    let cancelled = false
    setState({ topic: null, loading: true })
    ctx
      .loadTopic(ctx.sheetTopicId)
      .then((res) => {
        if (cancelled) return
        setState({
          topic: res.kind === 'topic' ? res.topic : null,
          loading: false,
        })
      })
      .catch(() => {
        if (cancelled) return
        setState({ topic: null, loading: false })
      })
    return () => {
      cancelled = true
    }
  }, [ctx, ctx.sheetTopicId])

  return {
    open: ctx.sheetOpen,
    topicId: ctx.sheetTopicId,
    topic: state.topic,
    loading: state.loading,
    openSheet: ctx.openSheet,
    closeSheet: ctx.closeSheet,
  }
}
